import React, { useState, useEffect } from 'react';
import { playerAttendanceService } from '../../services/playerAttendanceService';
import type { PlayerAttendance, AttendanceStats } from '../../types/playerCard';
import { Calendar, TrendingUp, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

interface AttendanceLogProps {
  playerId: string;
  teamId: string;
}

export const AttendanceLog: React.FC<AttendanceLogProps> = ({
  playerId,
  teamId,
}) => {
  const [attendance, setAttendance] = useState<PlayerAttendance[]>([]);
  const [stats, setStats] = useState<AttendanceStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAttendance();
  }, [playerId, teamId]);

  const loadAttendance = async () => {
    setLoading(true);
    try {
      const [history, attendanceStats] = await Promise.all([
        playerAttendanceService.getAttendanceHistory(playerId, teamId),
        playerAttendanceService.getAttendanceStats(playerId, teamId),
      ]);

      setAttendance(history);
      setStats(attendanceStats);
    } catch (error) {
      console.error('Error loading attendance:', error);
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: PlayerAttendance['status']) => {
    switch (status) {
      case 'late':
        return <Clock className="text-yellow-500" size={18} />;
      case 'no_show':
        return <XCircle className="text-red-500" size={18} />;
      case 'excused':
        return <AlertCircle className="text-blue-500" size={18} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: PlayerAttendance['status']) => {
    switch (status) {
      case 'late':
        return 'bg-yellow-100 text-yellow-800';
      case 'no_show':
        return 'bg-red-100 text-red-800';
      case 'excused':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: PlayerAttendance['status']) => {
    switch (status) {
      case 'late':
        return 'Late (after warmups)';
      case 'no_show':
        return 'No Show';
      case 'excused':
        return 'Excused Absence';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Attendance %</div>
            <div className="text-2xl font-bold text-primary">{stats.attendance_percentage}%</div>
            <div className="text-xs text-gray-500 mt-1">
              (Late & No Show count against)
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Practices</div>
            <div className="text-2xl font-bold text-gray-900">
              {stats.practices_present}/{stats.total_practices}
            </div>
            <div className="text-xs text-gray-500 mt-1">Present</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Derogatory</div>
            <div className="text-2xl font-bold text-red-600">
              {stats.practices_late + stats.practices_no_show}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {stats.practices_late} Late, {stats.practices_no_show} No Show
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Excused</div>
            <div className="text-2xl font-bold text-blue-600">{stats.practices_excused}</div>
            <div className="text-xs text-gray-500 mt-1">Not derogatory</div>
          </div>
        </div>
      )}

      {/* Attendance History */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar size={20} />
          Attendance History
        </h3>

        {attendance.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <CheckCircle className="mx-auto h-12 w-12 text-green-400 mb-4" />
            <p className="text-gray-600 font-medium">Perfect Attendance</p>
            <p className="text-sm text-gray-500 mt-1">
              No exceptions recorded. All practices/games attended.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {attendance.map((record) => (
              <div
                key={record.id}
                className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-4 flex-1">
                  {getStatusIcon(record.status)}
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-gray-900">
                        {record.event_type === 'practice' ? 'Practice' : 'Game'}
                      </span>
                      <span className="text-sm text-gray-600">
                        {format(new Date(record.event_date), 'MMM d, yyyy')}
                      </span>
                    </div>
                    {record.notes && (
                      <p className="text-sm text-gray-600 mt-1">{record.notes}</p>
                    )}
                  </div>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}
                >
                  {getStatusLabel(record.status)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

